import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Coffee, ShoppingBag, Truck, AlertCircle } from "lucide-react";
import { imgUri, useMenu, useOrderingStatus } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useCart } from "../lib/cart";
import { ProductCard } from "../components/ProductCard";
import { Header } from "../components/Header";
import { HeroCarousel } from "../components/HeroCarousel";
import { LegalFooter } from "../components/LegalFooter";

const PIZZA_SLUG = "pizza";
const CUSTOM_PIZZA_SLUG = "creer-votre-pizza";

function OrderTypeSelector({ compact }) {
  const { t } = useI18n();
  const { orderType, setOrderType } = useCart();
  const { data } = useMenu();
  const s = data?.settings;
  const items = [
    { key: "pickup", Icon: ShoppingBag, label: t("pickup"), hint: t("pickupHint"), enabled: s?.pickup_enabled !== false },
    { key: "delivery", Icon: Truck, label: t("delivery"), hint: t("deliveryHint"), enabled: s?.delivery_enabled !== false },
  ];
  return (
    <div className="flex gap-2.5" data-testid="order-type-selector">
      {items.map(({ key, Icon, label, hint, enabled }) => {
        const on = orderType === key;
        return (
          <button
            key={key}
            type="button"
            data-testid={`home-order-type-${key}`}
            disabled={!enabled}
            onClick={() => setOrderType(key)}
            className={`flex flex-1 items-center gap-2.5 rounded-2xl border-[1.5px] px-3 transition-all active:scale-[0.98] ${
              compact ? "h-14" : "h-[72px]"
            } ${on ? "border-hm-inverse bg-hm-inverse" : "border-hm-line bg-hm-card"} ${!enabled ? "opacity-40" : ""}`}
          >
            <span className={`flex h-[38px] w-[38px] items-center justify-center rounded-full ${on ? "bg-hm-brand" : "bg-hm-tertiary"}`}>
              <Icon size={18} className={on ? "text-white" : "text-hm-ink"} />
            </span>
            <span className="flex-1 text-left">
              <span className={`block font-sans text-[13px] font-extrabold tracking-wide ${on ? "text-white" : "text-hm-ink"}`}>
                {label.toUpperCase()}
              </span>
              {!compact ? <span className={`block font-sans text-[11px] ${on ? "text-white/80" : "text-hm-muted"}`}>{hint}</span> : null}
            </span>
            {on ? <CheckCircle size={18} className="text-white" /> : null}
          </button>
        );
      })}
    </div>
  );
}

export default function MenuPage() {
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const { data, isLoading, isError, refetch } = useMenu();
  const { data: ordering } = useOrderingStatus();
  const closedToday = !!ordering?.closed_today;
  const [cat, setCat] = useState("all");

  const customCat = data?.categories.find((c) => c.slug === CUSTOM_PIZZA_SLUG);
  const mainCats = useMemo(
    () => (data?.categories ?? []).filter((c) => !c.filter && c.active !== false && c.slug !== CUSTOM_PIZZA_SLUG),
    [data]
  );

  const sections = useMemo(() => {
    if (!data) return [];
    const cats = cat === "all" ? mainCats : mainCats.filter((c) => c.id === cat);
    return cats
      .map((c) => {
        let products = c.filter
          ? data.products.filter((p) => p.options.some((o) => o.key === c.filter))
          : data.products.filter((p) => p.category_id === c.id);
        if (c.slug === PIZZA_SLUG) {
          // Forced order inside Pizzas (exactly like the Mobile app):
          // 1) active Pizza du mois (highlight "moment")  2) Créer votre pizza  3) regular pizzas
          const month = data.products.filter((p) => p.highlight === "moment");
          const custom = customCat ? data.products.filter((p) => p.category_id === customCat.id && p.highlight !== "moment") : [];
          const rest = products.filter((p) => p.highlight !== "moment");
          products = [...month, ...custom, ...rest];
        } else {
          products = products.filter((p) => p.highlight !== "moment");
        }
        return { id: c.id, title: tx(c.name), count: products.length, products };
      })
      .filter((s) => s.count > 0);
  }, [data, cat, tx, mainCats, customCat]);

  const settings = data?.settings;
  const heroImages = settings?.hero_images ?? [];

  const orderNow = () => {
    const pizza = data?.categories.find((c) => c.slug === PIZZA_SLUG);
    if (pizza) setCat(pizza.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-hm-surface">
      <Header />

      {/* Category rail */}
      <div className="sticky top-14 z-20 border-b border-hm-line bg-hm-surface/95 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1120px] gap-2 overflow-x-auto px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Chip label={t("all")} on={cat === "all"} onClick={() => setCat("all")} testId="category-chip-all" />
          {mainCats.map((c) => (
            <Chip key={c.id} label={tx(c.name)} on={cat === c.id} onClick={() => setCat(c.id)} testId={`category-chip-${c.slug}`} />
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-hm-line border-t-hm-brand" />
        </div>
      ) : isError || !data ? (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6">
          <p className="font-sans text-base text-hm-muted">{t("loadError")}</p>
          <button
            type="button"
            data-testid="menu-retry-button"
            onClick={() => refetch()}
            className="rounded-full border-[1.5px] border-hm-lineStrong px-6 py-3 font-sans font-bold text-hm-ink"
          >
            {t("retry")}
          </button>
        </div>
      ) : (
        <div className="mx-auto w-full max-w-[1120px] px-4 pb-6">
          {closedToday ? (
            <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-hm-error p-3.5 text-white" data-testid="menu-closed-today">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <span className="font-sans text-sm font-bold">{t("closedToday")}</span>
            </div>
          ) : null}
          {cat === "all" ? (
            <>
              {settings?.temporarily_closed ? (
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-hm-error p-2.5 text-white" data-testid="closed-banner">
                  <AlertCircle size={16} />
                  <span className="font-sans font-bold">{t("closedNow")}</span>
                </div>
              ) : null}

              {heroImages.length > 0 ? <HeroCarousel images={heroImages} onClick={orderNow} /> : null}

              <div className="mt-4 flex items-center gap-3 rounded-3xl border border-hm-line bg-hm-card p-5 shadow-lg shadow-black/5">
                <div className="flex-1">
                  <p className="font-sans text-[10.5px] font-extrabold tracking-[2px] text-hm-brand">{t("heroKicker")}</p>
                  <h1 className="mt-1 font-display text-3xl leading-tight text-hm-ink sm:text-4xl">Hallo Magic Pizza</h1>
                  <p className="mt-2 max-w-lg font-sans text-sm leading-5 text-hm-ink">{t("heroSub")}</p>
                  <button
                    type="button"
                    data-testid="hero-order-now"
                    onClick={orderNow}
                    className="mt-4 inline-flex h-12 items-center gap-2 rounded-full bg-hm-brand px-5 font-sans text-[15px] font-extrabold text-white transition-transform active:scale-[0.98]"
                  >
                    {t("orderNowCta")} <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              <p className="mt-5 mb-2 font-sans text-xs font-bold uppercase tracking-wide text-hm-muted">{t("howToGet")}</p>
              <OrderTypeSelector />
            </>
          ) : (
            <div className="mt-4">
              <OrderTypeSelector compact />
            </div>
          )}

          {sections.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
              <Coffee size={40} className="text-hm-muted" />
              <p className="font-display text-xl text-hm-ink">{t("emptyCategory")}</p>
            </div>
          ) : (
            sections.map((section) => (
              <section key={section.id}>
                <div className="mt-8 mb-4 flex items-center gap-3.5">
                  <h2 className="font-display text-3xl text-hm-ink" data-testid={`section-${section.id}`}>{section.title}</h2>
                  <span className="h-px flex-1 bg-hm-lineStrong" />
                  <span className="font-sans text-[13px] font-bold text-hm-muted">{section.count}</span>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {section.products.map((p) => (
                    <ProductCard key={p.id} product={p} />
                  ))}
                </div>
              </section>
            ))
          )}

          <LegalFooter name={settings?.restaurant_name} />
        </div>
      )}
    </div>
  );
}

function Chip({ label, on, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`h-[38px] flex-shrink-0 whitespace-nowrap rounded-full px-4 font-sans text-sm font-bold transition-colors ${
        on ? "bg-hm-brand text-white" : "bg-hm-tertiary text-hm-ink"
      }`}
    >
      {label}
    </button>
  );
}
